// src/lib/trades/replayRange.ts
//
// Replay range configuration: how many trading days of history each replay
// timeframe is allowed to cover, plus trader-friendly labels for the UI.
//
// IMPORTANT: this module only caps the REPLAY range. The chart's loaded data
// window (server canonicalWindows / client tradeWindow BOT context) is left
// UNCHANGED — the chart still shows its normal context while the replay range
// itself is trimmed to the per-timeframe trading-day window below.

export type ReplayRangeTf = '1m' | '5m' | '15m' | '30m' | '60m' | '1d';

const TF_ALIASES: Record<string, ReplayRangeTf> = {
  '1m': '1m',
  '1min': '1m',
  '5m': '5m',
  '5min': '5m',
  '15m': '15m',
  '15min': '15m',
  '30m': '30m',
  '30min': '30m',
  '60m': '60m',
  '60min': '60m',
  '1h': '60m',
  '1d': '1d',
  '1day': '1d',
};

export function toReplayRangeTf(timeframe: string): ReplayRangeTf | null {
  const normalized = String(timeframe || '').trim().toLowerCase();
  return TF_ALIASES[normalized] ?? null;
}

// Trading days of replay history per timeframe. `0` means replay is disabled.
export const REPLAY_RANGE_TRADING_DAYS: Record<ReplayRangeTf, number> = {
  '1m': 1,
  '5m': 3,
  '15m': 5,
  '30m': 15,
  '60m': 15,
  '1d': 0,
};

// Trader-friendly replay range labels shown in the UI (replaces the technical
// "XXX bars" display). These are static per timeframe; the internal bar counts
// are intentionally unchanged.
export const REPLAY_RANGE_LABELS: Record<ReplayRangeTf, string> = {
  '1m': '1 Trading Day',
  '5m': '3 Trading Days',
  '15m': '5 Trading Days',
  '30m': '15 Trading Days',
  '60m': '15 Trading Days',
  '1d': 'Replay disabled',
};

export function getReplayRangeTradingDays(timeframe: string): number {
  const tf = toReplayRangeTf(timeframe);
  if (!tf) return 0;
  return REPLAY_RANGE_TRADING_DAYS[tf];
}

export function getReplayRangeLabel(timeframe: string): string | null {
  const tf = toReplayRangeTf(timeframe);
  if (!tf) return null;
  return REPLAY_RANGE_LABELS[tf];
}

export function isReplayTimeframeDisabled(timeframe: string): boolean {
  return getReplayRangeTradingDays(timeframe) <= 0;
}

// ---------------------------------------------------------------------------
// Trading-day cap computation (America/New_York, Mon-Fri trading days).
// Mirrors the trading-day model used by server canonical windows so the labels
// ("3 Trading Days", "5 Trading Days", ...) match the covered range.
// ---------------------------------------------------------------------------

const NY_TZ = 'America/New_York';

function nyPartsFromMs(ms: number): { y: number; m: number; d: number; hh: number; mm: number; ss: number } {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: NY_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(new Date(ms));
  const map: Record<string, string> = {};
  for (const part of parts) {
    if (part.type !== 'literal') map[part.type] = part.value;
  }
  return {
    y: Number(map.year),
    m: Number(map.month),
    d: Number(map.day),
    hh: Number(map.hour),
    mm: Number(map.minute),
    ss: Number(map.second),
  };
}

function nyLocalToUnixSec(y: number, m: number, d: number, hh = 0, mm = 0, ss = 0): number {
  const targetEpoch = Math.floor(Date.UTC(y, m - 1, d, hh, mm, ss) / 1000);
  let guess = targetEpoch;

  for (let i = 0; i < 6; i += 1) {
    const p = nyPartsFromMs(guess * 1000);
    const observedEpoch = Math.floor(Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mm, p.ss) / 1000);
    const delta = targetEpoch - observedEpoch;
    if (delta === 0) return guess;
    guess += delta;
  }

  return guess;
}

function isWeekendDay(y: number, m: number, d: number): boolean {
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return dow === 0 || dow === 6;
}

/**
 * Returns the NY-midnight unix-second boundary of the trading day that starts
 * the replay range: `tradingDays` trading days ending at the anchor's NY
 * trading day (inclusive). Weekends are not trading days, so a Saturday/Sunday
 * anchor counts the preceding Friday as its trading day.
 *
 * Example: anchor = Wednesday, tradingDays = 3 -> the window covers Mon, Tue,
 * Wed and the returned cap start is Monday's NY midnight.
 */
export function getReplayRangeCapStartSec(anchorMs: number, tradingDays: number): number {
  const days = Math.max(1, Math.floor(Number(tradingDays) || 1));

  let cursor = nyPartsFromMs(anchorMs);
  let remaining = days;

  while (remaining > 0) {
    if (!isWeekendDay(cursor.y, cursor.m, cursor.d)) remaining -= 1;
    if (remaining === 0) break;
    const dt = new Date(Date.UTC(cursor.y, cursor.m - 1, cursor.d - 1, 12, 0, 0));
    cursor = {
      y: dt.getUTCFullYear(),
      m: dt.getUTCMonth() + 1,
      d: dt.getUTCDate(),
      hh: 0,
      mm: 0,
      ss: 0,
    };
  }

  return nyLocalToUnixSec(cursor.y, cursor.m, cursor.d, 0, 0, 0);
}
